/**
 * Bike Form Page
 * ==============
 * Create or edit a motorcycle.
 */

import { useParams, useNavigate, Link } from 'react-router-dom'
import { useMotorcycle } from '@/hooks/useMotorcycles'
import { ArrowLeft, Bike } from 'lucide-react'

export default function BikeForm() {
  const { bikeId } = useParams<{ bikeId: string }>()
  const navigate = useNavigate()
  const isEditMode = !!bikeId

  // Only call useMotorcycle with a valid ID when editing
  const { data: bike, isLoading, error } = useMotorcycle(isEditMode ? bikeId! : '')

  if (isEditMode && error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load motorcycle</p>
      </div>
    )
  }

  if (isEditMode && isLoading) {
    return (
      <div className="space-y-6">
        {/* Header skeleton */}
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
          <div className="h-8 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        </div>
        {/* Form skeleton */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <div className="space-y-4">
            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <ArrowLeft size={20} className="text-gray-600 dark:text-gray-400" />
        </button>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/50 flex items-center justify-center">
            <Bike size={24} className="text-primary-600 dark:text-primary-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {isEditMode ? `Edit ${bike?.name || 'Motorcycle'}` : 'Add Motorcycle'}
          </h1>
        </div>
      </div>

      {/* Placeholder form */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8">
        <div className="text-center py-12">
          <Bike size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {isEditMode ? 'Edit Motorcycle' : 'Add New Motorcycle'}
          </h2>
          <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-6">
            {isEditMode
              ? `Edit details for ${bike?.name || 'this motorcycle'}.`
              : 'Add a new motorcycle to your collection.'}
          </p>
          <Link
            to="/motorcycles"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <ArrowLeft size={18} />
            Back to Dashboard
          </Link>
        </div>
      </div>
    </div>
  )
}
